import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import CountdownTimer from '../components/CountdownTimer';
import RegistrationMeter from '../components/RegistrationMeter';
import RegistrationModal from '../components/RegistrationModal';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface RegistrationFee {
  amount: number;
  currency: string;
  description: string;
}

interface RegistrationStep {
  title: string;
  description: string;
  date: string;
}

export default function Registration() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(false);
  const [totalCapacity, setTotalCapacity] = useState(250);
  const [currentRegistrations, setCurrentRegistrations] = useState(0);
  const [reservedSpots, setReservedSpots] = useState(0);
  const [isCapacityReached, setIsCapacityReached] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { scrollY } = useScroll();

  const [heroTitle, setHeroTitle] = useState("Player Registration");
  const [heroSubtitle, setHeroSubtitle] = useState("Join Africa's most prestigious golf tournament");
  const [registrationFee, setRegistrationFee] = useState<RegistrationFee>({
    amount: 50000,
    currency: "₦",
    description: "Per player, inclusive of all tournament amenities"
  });
  const [feeInclusions, setFeeInclusions] = useState<string[]>([]);
  const [eligibilityCriteria, setEligibilityCriteria] = useState<string[]>([]);
  const [registrationSteps, setRegistrationSteps] = useState<RegistrationStep[]>([]);
  const [rules, setRules] = useState<string[]>([]);
  const [importantNotice, setImportantNotice] = useState("");

  useEffect(() => {
    fetchRegistrationData();
    fetchRegistrationContent();
  }, []);

  useEffect(() => {
    if (!isLoading && !isRegistrationEnabled) {
      navigate('/', { replace: true });
    }
  }, [isLoading, isRegistrationEnabled, navigate]);

  const fetchRegistrationContent = async () => {
    try {
      const { data, error } = await supabase
        .from('registration_content')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      if (data) {
        data.forEach((item) => {
          switch (item.content_key) {
            case 'hero_title':
              setHeroTitle(item.content_value);
              break;
            case 'hero_subtitle':
              setHeroSubtitle(item.content_value);
              break;
            case 'registration_fee':
              setRegistrationFee(item.content_value);
              break;
            case 'fee_inclusions':
              setFeeInclusions(item.content_value);
              break;
            case 'eligibility_criteria':
              setEligibilityCriteria(item.content_value);
              break;
            case 'registration_steps':
              setRegistrationSteps(item.content_value);
              break;
            case 'tournament_rules':
              setRules(item.content_value);
              break;
            case 'important_notice':
              setImportantNotice(item.content_value);
              break;
          }
        });
      }
    } catch (error) {
      console.error('Error fetching registration content:', error);
    }
  };

  const fetchRegistrationData = async () => {
    try {
      setIsLoading(true);

      const { data: registrationEnabledSetting, error: regEnabledError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'registration_enabled')
        .maybeSingle();

      if (!regEnabledError && registrationEnabledSetting) {
        const enabled = typeof registrationEnabledSetting.value === 'boolean'
          ? registrationEnabledSetting.value
          : registrationEnabledSetting.value === 'true' || registrationEnabledSetting.value === true;
        setIsRegistrationEnabled(enabled);
      }

      const { data: tournaments, error: tournamentError } = await supabase
        .from('tournaments')
        .select('id, capacity, registration_open_date')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1);

      if (tournamentError) throw tournamentError;

      if (tournaments && tournaments.length > 0) {
        setTotalCapacity(tournaments[0].capacity);
        if (tournaments[0].registration_open_date) {
          const openingDate = new Date(tournaments[0].registration_open_date);
          setIsRegistrationOpen(new Date() >= openingDate);
        }
      }

      const tournamentId = tournaments?.[0]?.id;
      let playerCountQuery = supabase
        .from('players')
        .select('*', { count: 'exact', head: true });

      if (tournamentId) {
        playerCountQuery = playerCountQuery.eq('tournament_id', tournamentId);
      }

      const { count, error: countError } = await playerCountQuery;

      if (countError) throw countError;

      if (count !== null) {
        setCurrentRegistrations(count);
      }

      const { data: settingsData, error: settingsError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'reserved_spots')
        .maybeSingle();
        
      if (!settingsError && settingsData) {
        const reservedValue = typeof settingsData.value === 'number' 
          ? settingsData.value 
          : (typeof settingsData.value === 'string' 
            ? parseInt(settingsData.value) 
            : parseInt(settingsData.value.toString()));
            
        setReservedSpots(isNaN(reservedValue) ? 0 : reservedValue);
        
        // Calculate if capacity is reached (considering reserved spots)
        const effectiveCapacity = tournaments && tournaments.length > 0 
          ? tournaments[0].capacity - reservedValue
          : totalCapacity - reservedValue;
          
        setIsCapacityReached(count !== null && count >= effectiveCapacity);
      } else {
        // If no reserved spots setting, just check against total capacity
        setIsCapacityReached(count !== null && count >= totalCapacity);
      }
    } catch (error) {
      console.error('Error fetching registration data:', error);
      toast.error('Failed to load registration data');
    } finally {
      setIsLoading(false);
    }
  };

  useMotionValueEvent(scrollY, "change", (latest) => {
    setShowFloatingButton(latest > window.innerHeight);
  });

  const handleRegisterClick = () => {
    if (!isRegistrationEnabled) {
      toast.error('Registration is currently disabled. Please check back later.');
      return;
    }

    if (isCapacityReached) {
      toast.error('Registration is closed. Maximum capacity has been reached.');
      return;
    }

    setIsModalOpen(true);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-golf-gray text-white py-24">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center px-2 sm:px-0"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6">
              {heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-golf-sand mb-8">
              {heroSubtitle}
            </p>
            <div className="max-w-lg mx-auto px-2 sm:px-0">
              {isLoading ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
                </div>
              ) : isRegistrationEnabled && !isRegistrationOpen ? (
                <CountdownTimer />
              ) : isRegistrationEnabled && isRegistrationOpen ? (
                <RegistrationMeter
                  totalCapacity={totalCapacity}
                  currentRegistrations={currentRegistrations}
                  reservedSpots={reservedSpots}
                  className="bg-white/10 backdrop-blur-sm border border-white/20"
                  showButton={true}
                  onRegister={handleRegisterClick}
                />
              ) : (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <p className="text-center text-white text-lg">
                    Registration is currently disabled
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Registration Fee Section */}
      <section className="py-16 bg-golf-sand">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center px-2 sm:px-0"
          >
            <div className="bg-white rounded-2xl shadow-lg p-5 md:p-8">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-golf-gray mb-4 md:mb-6">
                Registration Fee
              </h2>
              <div className="text-3xl md:text-5xl lg:text-6xl font-display font-bold text-golf-orange mb-3 md:mb-4">
                {registrationFee.currency}{registrationFee.amount.toLocaleString()}
              </div>
              <p className="text-gray-600 mb-6 md:mb-8 text-sm md:text-base">
                {registrationFee.description}
              </p>
              {feeInclusions.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 text-left">
                  {feeInclusions.map((inclusion, index) => (
                    <div key={index} className="flex items-center gap-2 text-golf-gray">
                      <CheckIcon className="w-5 h-5 text-golf-orange flex-shrink-0" />
                      <span>{inclusion}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Registration Process */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto px-2 sm:px-0"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center text-golf-gray mb-8 md:mb-16">
              Registration Process
            </h2>
            
            <div className="space-y-8">
              {registrationSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-md p-4 sm:p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-golf-orange text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-golf-gray mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 mb-2">{step.description}</p>
                      <p className="text-xs sm:text-sm text-golf-orange font-medium">{step.date}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Eligibility & Rules */}
      <section className="py-16 bg-golf-sand">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto px-2 sm:px-0">
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {/* Eligibility Criteria */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="bg-white rounded-2xl shadow-lg p-5 sm:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-display font-bold text-golf-gray mb-4 sm:mb-6">
                  Eligibility Criteria
                </h3>
                <div className="space-y-4">
                  {eligibilityCriteria.map((criteria, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-golf-orange flex-shrink-0 mt-1" />
                      <p className="text-sm sm:text-base text-gray-600">{criteria}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Tournament Rules */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-lg p-5 sm:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-display font-bold text-golf-gray mb-4 sm:mb-6">
                  Tournament Rules
                </h3>
                <div className="space-y-4">
                  {rules.map((rule, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-golf-orange flex-shrink-0 mt-1" />
                      <p className="text-sm sm:text-base text-gray-600">{rule}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto px-2 sm:px-0"
          >
            <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8">
              <div className="flex items-start gap-4">
                <ExclamationTriangleIcon className="w-6 h-6 text-golf-orange flex-shrink-0" />
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-golf-gray mb-2">
                    Important Notice
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    {importantNotice || `Registration is on a first-come, first-served basis. The tournament is limited to ${totalCapacity} players${reservedSpots > 0 ? ` (with ${reservedSpots} spots reserved for special registrations)` : ''}. Early registration is recommended to secure your spot.`}
                  </p>
                  {!isRegistrationEnabled && (
                    <p className="mt-4 text-red-600 font-medium text-sm sm:text-base">
                      Registration is currently disabled by the tournament organizers. Please check back later for updates.
                    </p>
                  )}
                  {isRegistrationEnabled && isCapacityReached && (
                    <p className="mt-4 text-red-600 font-medium text-sm sm:text-base">
                      Registration is currently closed as we have reached maximum capacity. Please check back later for any updates.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          fetchRegistrationData(); // Refresh data when modal closes
        }}
      />

      {/* Floating Register Button */}
      {isRegistrationEnabled && isRegistrationOpen && !isCapacityReached && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ 
            opacity: showFloatingButton ? 1 : 0,
            y: showFloatingButton ? 0 : 100
          }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-8 right-8 z-50"
        >
          <button
            onClick={handleRegisterClick}
            className="modern-button px-6 sm:px-8 py-3 sm:py-4 text-white text-base sm:text-lg font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            Register Now
          </button>
        </motion.div>
      )}
    </div>
  );
}